import { useTranslations } from "next-intl";
import type { EffectiveSender } from "@/lib/utils/sender-display";

/**
 * The send dialogs' `From:` row (PSendEmail, PSendEmailToGuest).
 *
 * For an ordinary sender this is exactly the old line: `From: <address>`. For
 * an address that goes out ON BEHALF (sender on behalf, 2026-09-29) the row
 * names the rewritten mailbox the recipient will see, marks it "(on behalf)",
 * and a note underneath says where replies go and — the safeguard sentence —
 * how the fallbacks look: a large send, and any send once Veltric's shared
 * daily on-behalf volume is spent, goes out as `noreply@veltric.app`, an
 * outage (kill switch, trip) from the address itself, and replies go to the
 * same place either way. The cap is ONE counter for the whole platform (all
 * tenants), so a single guest mail can hit it after other tenants' big sends —
 * "for large sends" alone was too narrow (audit r2 R2FE-1); and a bulk job
 * larger than the cap goes out ENTIRELY as noreply@ even on an empty day, so
 * a large send is a case of its own, not an example of a spent volume (audit
 * r4 R4F-1). The UI cannot see the cap, kill switch or trip,
 * so it states the fallbacks instead of promising the rewrite (the old
 * "outages ⇒ noreply@" was wrong, audit r1 L7-1).
 *
 * Reply target: the template's `replyToMail` when set (the consumer writes it
 * over the resolver's Reply-To), else the real address.
 */
export const SenderFromLine: React.FC<{
  /** The dialog's own "From" label. */
  label: string;
  /** `null` = the sender meta could not be read. */
  sender: EffectiveSender | null;
  /** Shown instead of an address when `sender` is null. */
  unavailableText: string;
  /** The template's own Reply-To, free text; blank = none. */
  replyToMail?: string | null;
  className: string;
}> = ({ label, sender, unavailableText, replyToMail, className }) => {
  const tSend = useTranslations("pSendEmail");
  const tSender = useTranslations("sender");
  const onBehalf = sender?.onBehalf;
  return (
    <>
      <div className={className}>
        {label}: {sender ? sender.address : unavailableText}
        {onBehalf ? ` (${tSender("onBehalf")})` : null}
      </div>
      {onBehalf && (
        <p
          className="body-3-regular font-secondary min-w-0 break-words"
          data-testid="sender-on-behalf-from-note"
        >
          {tSend("fromOnBehalfNote", {
            address: replyToMail?.trim() || onBehalf.replyTo,
          })}
        </p>
      )}
    </>
  );
};
